import type { AmbientFishConfig } from "./SceneProps";

export const DEFAULT_POLLUTED_AMBIENT_SHOAL_COUNT = 2;
export const DEFAULT_CLEAN_AMBIENT_SHOAL_COUNT = 8;
export const DEFAULT_AMBIENT_FISH_PER_SHOAL = 3;
export const DEFAULT_POLLUTED_AMBIENT_FISH_COUNT = 6;
export const DEFAULT_CLEAN_AMBIENT_FISH_COUNT = 24;
const MAX_AMBIENT_FISH_COUNT = 512;
const MAX_AMBIENT_SHOAL_COUNT = 64;
const MAX_AMBIENT_FISH_PER_SHOAL = 64;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));
const clamp01 = (value: number) => clamp(value, 0, 1);
const lerp = (from: number, to: number, progress: number) =>
  from + (to - from) * progress;

function normalizeCount(value: number | undefined, fallback: number, max: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return fallback;
  }

  return Math.round(clamp(value, 0, max));
}

export function getAmbientFishMaxShoalCount(config: AmbientFishConfig) {
  const shoals = config.shoals;
  if (!shoals) return 0;

  const configuredCount = normalizeCount(shoals.count, 0, MAX_AMBIENT_SHOAL_COUNT);
  const pollutionShoals = config.pollutionShoals;
  if (!pollutionShoals) return configuredCount;

  return Math.max(
    configuredCount,
    normalizeCount(
      pollutionShoals.pollutedCount,
      DEFAULT_POLLUTED_AMBIENT_SHOAL_COUNT,
      MAX_AMBIENT_SHOAL_COUNT
    ),
    normalizeCount(
      pollutionShoals.cleanCount,
      DEFAULT_CLEAN_AMBIENT_SHOAL_COUNT,
      MAX_AMBIENT_SHOAL_COUNT
    )
  );
}

export function getAmbientFishMaxCount(config: AmbientFishConfig) {
  if (config.shoals) {
    return getAmbientFishShoalFishCounts(config).reduce(
      (sum, count) => sum + count,
      0
    );
  }

  const configuredCount = normalizeCount(config.count, 0, MAX_AMBIENT_FISH_COUNT);
  const pollutionCounts = config.pollutionCounts;
  if (!pollutionCounts) return configuredCount;

  return Math.max(
    configuredCount,
    normalizeCount(
      pollutionCounts.pollutedCount,
      DEFAULT_POLLUTED_AMBIENT_FISH_COUNT,
      MAX_AMBIENT_FISH_COUNT
    ),
    normalizeCount(
      pollutionCounts.cleanCount,
      DEFAULT_CLEAN_AMBIENT_FISH_COUNT,
      MAX_AMBIENT_FISH_COUNT
    )
  );
}

export function getAmbientFishCountForPollution(
  config: AmbientFishConfig,
  pollutionLevel: number
) {
  if (config.shoals) {
    return getAmbientFishShoalFishCounts(config)
      .slice(0, getAmbientFishShoalCountForPollution(config, pollutionLevel))
      .reduce((sum, count) => sum + count, 0);
  }

  const maxCount = getAmbientFishMaxCount(config);
  const pollutionCounts = config.pollutionCounts;
  if (!pollutionCounts) return normalizeCount(config.count, 0, maxCount);

  const pollutedCount = normalizeCount(
    pollutionCounts.pollutedCount,
    DEFAULT_POLLUTED_AMBIENT_FISH_COUNT,
    maxCount
  );
  const cleanCount = normalizeCount(
    pollutionCounts.cleanCount,
    DEFAULT_CLEAN_AMBIENT_FISH_COUNT,
    maxCount
  );
  const dirtyProgress = Number.isFinite(pollutionLevel) ? pollutionLevel : 0;

  return Math.round(
    clamp(lerp(cleanCount, pollutedCount, clamp01(dirtyProgress)), 0, maxCount)
  );
}

export function getAmbientFishShoalCountForPollution(
  config: AmbientFishConfig,
  pollutionLevel: number
) {
  const shoals = config.shoals;
  if (!shoals) return 0;

  const maxShoalCount = getAmbientFishMaxShoalCount(config);
  const pollutionShoals = config.pollutionShoals;
  if (!pollutionShoals) {
    return normalizeCount(shoals.count, 0, maxShoalCount);
  }

  const pollutedCount = normalizeCount(
    pollutionShoals.pollutedCount,
    DEFAULT_POLLUTED_AMBIENT_SHOAL_COUNT,
    maxShoalCount
  );
  const cleanCount = normalizeCount(
    pollutionShoals.cleanCount,
    DEFAULT_CLEAN_AMBIENT_SHOAL_COUNT,
    maxShoalCount
  );
  const dirtyProgress = Number.isFinite(pollutionLevel) ? pollutionLevel : 0;

  return Math.round(
    clamp(lerp(cleanCount, pollutedCount, clamp01(dirtyProgress)), 0, maxShoalCount)
  );
}

export function getAmbientFishShoalFishCounts(config: AmbientFishConfig) {
  const shoals = config.shoals;
  if (!shoals) return [];

  const maxShoalCount = getAmbientFishMaxShoalCount(config);
  const fishPerShoal = normalizeCount(
    shoals.fishPerShoal,
    DEFAULT_AMBIENT_FISH_PER_SHOAL,
    MAX_AMBIENT_FISH_PER_SHOAL
  );

  return Array.from({ length: maxShoalCount }, (_, index) =>
    normalizeCount(shoals.fishCounts?.[index], fishPerShoal, MAX_AMBIENT_FISH_PER_SHOAL)
  );
}
